import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';

import { forkJoin, from, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { NavigationLink } from '@stylehaven/components';

import {
    ApiFragment,
    defaultNavigationOptions,
    NavigationOptions,
    NavigationQuery,
    RecordNavigationService,
    RecordService,
    RecordStatus,
    RecordType,
    ToastHelper,
    VersioningService,
    WorkflowTask
} from '@digihaven/shared';

import { Series } from '@common/entities/series/series.entity';
import { Task } from '@common/entities/task/task.entity';

import { isString } from 'lodash-es';
import { SeriesService } from '../series/series.service';

@Injectable()
export class NavigationService extends RecordNavigationService {
    constructor(
        protected router: Router,
        private seriesService: SeriesService,
        protected recordService: RecordService,
        versioningService: VersioningService,
        protected toastHelper: ToastHelper
    ) {
        super(recordService, versioningService, router, toastHelper);
    }

    private getStatus(record: ApiFragment | Series): RecordStatus {
        return record instanceof ApiFragment ? record.getStatus() : record.Status.Status;
    }

    private getRecordType(record: ApiFragment | Series): RecordType {
        return record instanceof ApiFragment ? record.getRecordType() : record.RecordType;
    }

    protected getSeriesId(subject: ApiFragment | Series) {
        return subject instanceof ApiFragment ? subject.getVersioningId() : subject.Id;
    }

    private getSeriesVersion(subject: ApiFragment | Series, version: number | undefined) {
        if (version !== undefined) {
            return version.toString();
        }
        return subject instanceof ApiFragment ? subject.getVersioningVersion() : subject.Version.toString();
    }

    getTaskLessNavigationPath(
        record: ApiFragment | Series,
        options: NavigationOptions = defaultNavigationOptions
    ): string[] {
        if (record instanceof ApiFragment && this.getRecordType(record) === RecordType.BulkSeries) {
            return ['/concepts', 'processing-list', record.getRecordId()];
        }

        switch (this.getStatus(record)) {
            case RecordStatus.Assigned:
            case RecordStatus.Declined:
            case RecordStatus.DraftValid:
            case RecordStatus.DraftInvalid:
            case RecordStatus.Evaluated:
            case RecordStatus.Submitted:
            case RecordStatus.SubmittedForPublication:
            case RecordStatus.RejectedForCorrection:
            case RecordStatus.Rejected: {
                return [
                    '/concepts',
                    this.getSeriesId(record),
                    'v',
                    this.getSeriesVersion(record, options.version),
                    this.getTabOrDefault(options),
                    ...(options.editMode ? ['edit'] : [])
                ];
            }
            case RecordStatus.Expired:
            case RecordStatus.Published:
            default: {
                const isSeriesTemplate = this.getRecordType(record) === RecordType.SeriesTemplate;
                const publishedSubRoute = isSeriesTemplate ? 'templates' : 'series';
                return [
                    '/published',
                    publishedSubRoute,
                    this.getSeriesId(record),
                    'v',
                    this.getSeriesVersion(record, options.version),
                    this.getTabOrDefault(options),
                    ...(options.editMode ? ['edit'] : [])
                ];
            }
        }
    }

    getTaskNavigationPath(record: ApiFragment, taskId: string): string[] {
        return ['/evaluate', 'tasks', taskId, record.getVersioningId(), 'v', record.getVersioningVersion()];
    }

    private getTabOrDefault(options: NavigationOptions): string {
        return options.tab ? options.tab : 'details';
    }

    navigateTo(
        record: ApiFragment | Series,
        options: NavigationOptions = defaultNavigationOptions
    ): Observable<boolean> {
        if (record instanceof ApiFragment && this.getTaskList(record).length > 0) {
            return this.getTaskRoute(record, options);
        }

        const taskLessNavigationPath = this.getTaskLessNavigationPath(record, options);
        return this.navigateToPath(taskLessNavigationPath, options);
    }

    private navigateToPath(path: string[], options: NavigationOptions): Observable<boolean> {
        const routerNavigationOptions = this.mapOptionsToNavigationExtras(options);

        return from(this.router.navigate(path, routerNavigationOptions));
    }

    private mapOptionsToNavigationExtras(options: NavigationOptions): NavigationExtras {
        return {
            ...(!!options.state && { state: options.state })
        };
    }

    private getTaskRoute(record: ApiFragment, options: NavigationOptions): Observable<boolean> {
        const tasks: WorkflowTask[] = this.getTaskList(record);

        if (tasks.length === 1) {
            return this.navigateToTask(tasks[0].Id, record, options);
        }

        return forkJoin(this.fetchTasks(tasks, record)).pipe(
            map(taskList => taskList.find(task => task.Completable) || taskList[0]),
            switchMap(task => this.navigateToTask(task.Id, record, options))
        );
    }

    private fetchTasks(tasks: WorkflowTask[], record: ApiFragment): Observable<Task>[] {
        return tasks.map(task => this.seriesService.getTask(record.getVersioningId(), task.Id));
    }

    private getTaskList(record: ApiFragment): WorkflowTask[] {
        return record.metadata.Workflow?.Tasks?.Task || [];
    }

    private navigateToTask(taskId: string, record: ApiFragment, options: NavigationOptions): Observable<boolean> {
        const extras = this.mapOptionsToNavigationExtras(options);

        return from(this.router.navigate(this.getTaskNavigationPath(record, taskId), extras));
    }

    getNavigationLink(navigationQuery: NavigationQuery<ApiFragment | string | Series>): NavigationLink {
        if (isString(navigationQuery.record)) {
            return super.getNavigationLink(navigationQuery as NavigationQuery<string>);
        }

        if (navigationQuery.record instanceof ApiFragment) {
            const tasks = this.getTaskList(navigationQuery.record);
            if (tasks.length > 0) {
                return {
                    route: this.router
                        .createUrlTree(this.getTaskNavigationPath(navigationQuery.record, tasks[0].Id))
                        .toString(),
                    target: navigationQuery.navigationOptions?.target,
                    extras: {
                        state: navigationQuery.navigationOptions?.state,
                        queryParams: navigationQuery.navigationOptions?.queryParams
                    }
                };
            }
        }
        const path: string[] = this.getTaskLessNavigationPath(
            navigationQuery.record,
            navigationQuery.navigationOptions
        );
        return {
            route: this.router.createUrlTree(path).toString(),
            target: navigationQuery.navigationOptions?.target,
            extras: {
                state: navigationQuery.navigationOptions?.state,
                queryParams: navigationQuery.navigationOptions?.queryParams
            }
        };
    }

    navigate(query: NavigationQuery<ApiFragment | Series>): Observable<boolean> {
        return this.navigateTo(query.record, query.navigationOptions);
    }
}
